/**
 * Journey Calculator - Main Orchestrator
 *
 * Computes all deterministic data for the Journey tab.
 * This data is passed to the LLM which only generates narrative (not numbers).
 *
 * Architecture:
 * User Input → Journey Calculator → ComputedJourneyData → LLM (narrative) → Validator → Output
 */

import type { CalculatorState, RSUGrant } from '@/types'
import { quickRNOREstimate, type QuickRNORResult } from './rnorCalculator'
import { calculateTaxSavingsBreakdown, type TaxSavingsBreakdown, type TaxSavingsInput } from './taxSavingsCalculator'
import { calculateSSEligibility, type SSEligibilityResult } from './socialSecurityCalculator'
import { generateScenarios, type ScenarioComparison, type ScenarioInput } from './scenarioGenerator'
import { getEffectiveUSAAssets, getTotalResidentLiquidAssets } from '@/lib/utils/assetHelpers'
import { getCountryConfig } from '@/config/countries/registry'
import { defaults } from '@/data'

// Default exchange rate from config (used as fallback only - live rate fetched from API)
const DEFAULT_EXCHANGE_RATE = defaults.profile.exchangeRate

// ==========================================
// Types
// ==========================================

export interface Milestone {
  id: string
  date: string           // "March 15, 2025"
  title: string
  description: string
  category: 'preparation' | 'transition' | 'rnor' | 'post_rnor' | 'compliance'
  isDeadline: boolean
  daysFromNow: number
  urgency: 'critical' | 'high' | 'medium' | 'low'
}

export interface RiskFlag {
  id: string
  type: 'concentration' | 'timing' | 'currency' | 'compliance' | 'opportunity'
  severity: 'high' | 'medium' | 'low'
  title: string
  description: string
  recommendation: string
  relatedAsset?: string
  amountAtRisk?: number
}

// Insight seeds for LLM to reference
export interface InsightSeed {
  type: 'opportunity' | 'warning' | 'tip'
  title: string
  keyData: string          // The specific data point to highlight
  relatedAsset?: string
  amountFormatted?: string
  urgency?: 'immediate' | 'before_return' | 'during_rnor' | 'ongoing'
}

export interface LiquidationItem {
  id: string
  sequence: number
  asset: string
  action: string
  timing: 'before_move' | 'during_rnor' | 'post_rnor'
  timingDescription: string
  reason: string
  estimatedValueUSD: number
  estimatedValueINR: number
  taxImplication: string
  requiresCAReview: boolean
}

// Confidence score breakdown for transparency
export interface ConfidenceScoreBreakdown {
  overall: number                    // 0-100 weighted average
  level: 'high' | 'medium' | 'low'   // Categorical
  components: {
    rnorCertainty: number            // 0-100: RNOR eligibility confidence
    assetDataCompleteness: number    // 0-100: How complete is financial data
    taxCalculationAccuracy: number   // 0-100: RSU assumptions, etc.
    projectionReliability: number    // 0-100: Market/timeline uncertainty
  }
  explanation: string
  limitations: string[]
  improvementSuggestions: string[]
}

export interface ComputedJourneyData {
  // Metadata
  computedAt: string
  dataVersion: string

  // Dates (deterministic) - All specific dates for LLM to use
  dates: {
    currentDate: string
    currentDateFormatted: string
    targetMoveDate: string
    targetMoveDateFormatted: string
    sixMonthsBeforeMove: string
    sixMonthsBeforeMoveFormatted: string
    threeMonthsBeforeMove: string
    threeMonthsBeforeMoveFormatted: string
    rnorStartDate: string | null
    rnorStartDateFormatted: string | null
    rnorEndDate: string | null
    rnorEndDateFormatted: string | null
    daysUntilMove: number
    monthsUntilMove: number
    fiscalYearOfReturn: string
  }

  // RNOR Status
  rnor: QuickRNORResult | null

  // Tax Savings Breakdown
  taxSavings: TaxSavingsBreakdown

  // Social Security (USA only)
  socialSecurity: SSEligibilityResult | null

  // 3 Scenarios Comparison
  scenarios: ScenarioComparison

  // Liquidation Sequence
  liquidationSequence: LiquidationItem[]

  // Key Milestones with specific dates
  milestones: Milestone[]

  // Risk Flags
  riskFlags: RiskFlag[]

  // Insight seeds - pre-computed data points for LLM to reference
  insightSeeds: InsightSeed[]

  // Summary stats for hero section
  heroStats: {
    totalTaxSavingsINR: number
    totalTaxSavingsFormatted: string
    rnorYears: number
    rnorEligible: boolean
    ssEligible: boolean
    ssLifetimeValueINR?: number
    recommendedScenario: string
    daysUntilMove: number
  }

  // Deterministic confidence score (new)
  confidenceScore: ConfidenceScoreBreakdown
}

// ==========================================
// Main Calculator
// ==========================================

export function computeJourneyData(state: CalculatorState): ComputedJourneyData {
  const { profile, incomeExpenses } = state
  // Use helper to get effective USA assets (from residentCountryAssets or legacy usaAssets)
  const usaAssets = getEffectiveUSAAssets(state)
  const currentDate = new Date()
  const currentYear = currentDate.getFullYear()

  // ==========================================
  // 1. Calculate RNOR Status
  // ==========================================
  let rnor: QuickRNORResult | null = null
  if (profile.nriSinceYear && profile.expectedReturnYear) {
    rnor = quickRNOREstimate(profile.nriSinceYear, profile.expectedReturnYear)
  }

  // ==========================================
  // 2. Calculate Dates
  // ==========================================
  const targetYear = profile.expectedReturnYear || currentYear + 2
  const targetMonth = 4 // Default to April (start of Indian FY)
  const targetDate = new Date(targetYear, targetMonth - 1, 1)
  const daysUntilMove = Math.max(0, Math.floor((targetDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24)))
  const monthsUntilMove = Math.max(0, Math.floor(daysUntilMove / 30))

  // Calculate key milestone dates
  const sixMonthsBefore = new Date(targetDate)
  sixMonthsBefore.setMonth(sixMonthsBefore.getMonth() - 6)

  const threeMonthsBefore = new Date(targetDate)
  threeMonthsBefore.setMonth(threeMonthsBefore.getMonth() - 3)

  // Calculate RNOR dates
  const rnorEndYear = targetYear + (rnor?.estimatedYears || 0)
  const rnorEndDateObj = rnor?.eligible ? new Date(rnorEndYear, 2, 31) : null // March 31

  const dates = {
    currentDate: currentDate.toISOString(),
    currentDateFormatted: formatDate(currentDate),
    targetMoveDate: targetDate.toISOString(),
    targetMoveDateFormatted: formatDate(targetDate),
    sixMonthsBeforeMove: sixMonthsBefore.toISOString(),
    sixMonthsBeforeMoveFormatted: formatDate(sixMonthsBefore),
    threeMonthsBeforeMove: threeMonthsBefore.toISOString(),
    threeMonthsBeforeMoveFormatted: formatDate(threeMonthsBefore),
    rnorStartDate: rnor?.eligible ? targetDate.toISOString() : null,
    rnorStartDateFormatted: rnor?.eligible ? formatDate(targetDate) : null,
    rnorEndDate: rnorEndDateObj ? rnorEndDateObj.toISOString() : null,
    rnorEndDateFormatted: rnorEndDateObj ? formatDate(rnorEndDateObj) : null,
    daysUntilMove,
    monthsUntilMove,
    fiscalYearOfReturn: getFY(targetYear, targetMonth)
  }

  // ==========================================
  // 3. Calculate Tax Savings
  // ==========================================
  const totalForeignAssets = calculateTotalForeignAssets(state)
  const { assumptions } = state

  const taxInput: TaxSavingsInput = {
    residentCountry: profile.residentCountry as 'USA' | 'UAE',
    exchangeRate: profile.exchangeRate,
    stateTaxTier: usaAssets.stateTaxTier,
    rsuGrants: usaAssets.rsuGrants,
    esppBalance: usaAssets.esppBalance,
    balance401k: usaAssets.balance401k,
    balanceTraditionalIRA: usaAssets.balanceTraditionalIRA,
    taxableBrokerage: usaAssets.taxableBrokerage,
    brokerageCostBasis: usaAssets.brokerageCostBasis,
    cryptoBalance: usaAssets.crypto, // Crypto for tax arbitrage calculation
    rnorEligible: rnor?.eligible || false,
    rnorYears: rnor?.estimatedYears || 0,
    totalForeignAssets,
    // Pass user's SWR from assumptions (Step 7)
    safeWithdrawalRate: assumptions.safeWithdrawalRate
  }

  const taxSavings = calculateTaxSavingsBreakdown(taxInput)

  // ==========================================
  // 4. Calculate Social Security (for countries with SS system)
  // ==========================================
  const ssConfig = getCountryConfig(profile.residentCountry as 'USA' | 'UAE')
  let socialSecurity: SSEligibilityResult | null = null
  if (ssConfig.benefits.hasSocialSecurity) {
    socialSecurity = calculateSSEligibility(
      profile.yearsWorkedInUSA,
      profile.currentAge,
      incomeExpenses.annualIncomeUSD + (incomeExpenses.annualRSUIncomeUSD || 0),
      profile.exchangeRate
    )
  }

  // ==========================================
  // 5. Generate 3 Scenarios
  // ==========================================
  const scenarioInput: ScenarioInput = {
    nriSinceYear: profile.nriSinceYear || currentYear - 5,
    expectedReturnYear: targetYear,
    expectedReturnMonth: targetMonth,
    currentAge: profile.currentAge,
    residentCountry: profile.residentCountry as 'USA' | 'UAE',
    exchangeRate: profile.exchangeRate,
    totalForeignAssets,
    stateTaxTier: usaAssets.stateTaxTier,
    rsuGrants: usaAssets.rsuGrants,
    esppBalance: usaAssets.esppBalance,
    balance401k: usaAssets.balance401k,
    balanceTraditionalIRA: usaAssets.balanceTraditionalIRA,
    taxableBrokerage: usaAssets.taxableBrokerage,
    brokerageCostBasis: usaAssets.brokerageCostBasis,
    cryptoBalance: usaAssets.crypto,  // Crypto for tax arbitrage
    yearsWorkedInUSA: profile.yearsWorkedInUSA,
    annualIncomeUSD: incomeExpenses.annualIncomeUSD + (incomeExpenses.annualRSUIncomeUSD || 0),
    monthlySavingsUSD: incomeExpenses.monthlySavingsUSD || 5000,
    safeWithdrawalRate: assumptions.safeWithdrawalRate
  }

  const scenarios = generateScenarios(scenarioInput)

  // ==========================================
  // 6. Generate Liquidation Sequence
  // ==========================================
  const liquidationSequence = generateLiquidationSequence(state, rnor)

  // ==========================================
  // 7. Generate Milestones
  // ==========================================
  const milestones = generateMilestones(state, dates, rnor)

  // ==========================================
  // 8. Generate Risk Flags
  // ==========================================
  const riskFlags = generateRiskFlags(state, totalForeignAssets)

  // ==========================================
  // 9. Generate Insight Seeds for LLM
  // ==========================================
  const insightSeeds = generateInsightSeeds(state, taxSavings, socialSecurity, riskFlags, rnor)

  // ==========================================
  // 10. Compile Hero Stats
  // ==========================================
  const recommendedScenario = scenarios.scenarios.find(s => s.isRecommended)
  const heroStats: ComputedJourneyData['heroStats'] = {
    totalTaxSavingsINR: taxSavings.total,
    totalTaxSavingsFormatted: taxSavings.totalFormatted,
    rnorYears: rnor?.estimatedYears || 0,
    rnorEligible: rnor?.eligible || false,
    ssEligible: socialSecurity?.eligible || false,
    ssLifetimeValueINR: socialSecurity?.lifetimeValueINR,
    recommendedScenario: recommendedScenario?.name || 'Current Plan',
    daysUntilMove
  }

  // ==========================================
  // 11. Calculate Deterministic Confidence Score
  // ==========================================
  const confidenceScore = calculateConfidenceScore(state, rnor, taxSavings, usaAssets)

  return {
    computedAt: new Date().toISOString(),
    dataVersion: '1.0',
    dates,
    rnor,
    taxSavings,
    socialSecurity,
    scenarios,
    liquidationSequence,
    milestones,
    riskFlags,
    insightSeeds,
    heroStats,
    confidenceScore
  }
}

// ==========================================
// Helper Functions
// ==========================================

function calculateTotalForeignAssets(state: CalculatorState): number {
  // For ALL countries, "foreign assets" means assets in the user's current resident country
  // (which will become "foreign" relative to India after they move)
  // This works for USA, UAE, and any future country using the country-agnostic store
  const { residentCountryAssets } = state

  // Get liquid assets (retirement, investments, RSUs, cash, etc.)
  const liquidAssets = getTotalResidentLiquidAssets(state)

  // Add real estate equity (stored in residentCountryAssets.assets)
  const assets = residentCountryAssets?.assets || {}
  const primaryHomeEquity = Math.max(0, (assets.primaryHomeValue || 0) - (assets.primaryHomeMortgage || 0))
  const rentalEquity = Math.max(0, (assets.rentalPropertyValue || 0) - (assets.rentalPropertyMortgage || 0))

  return liquidAssets + primaryHomeEquity + rentalEquity
}

function generateLiquidationSequence(
  state: CalculatorState,
  rnor: QuickRNORResult | null
): LiquidationItem[] {
  const { residentCountryAssets, profile } = state
  // Use helper to get effective USA assets
  const usaAssets = getEffectiveUSAAssets(state)
  const exchangeRate = profile.exchangeRate || DEFAULT_EXCHANGE_RATE
  const items: LiquidationItem[] = []
  const isUSA = profile.residentCountry === 'USA'
  const isUAE = profile.residentCountry === 'UAE'
  let sequence = 1

  // Calculate target move date and key milestone dates
  const currentYear = new Date().getFullYear()
  const targetYear = profile.expectedReturnYear || currentYear + 2
  const targetMonth = 4 // April
  const targetDate = new Date(targetYear, targetMonth - 1, 1)

  // Key dates for liquidation
  const sixMonthsBefore = new Date(targetDate)
  sixMonthsBefore.setMonth(sixMonthsBefore.getMonth() - 6)

  const threeMonthsBefore = new Date(targetDate)
  threeMonthsBefore.setMonth(threeMonthsBefore.getMonth() - 3)

  // Format date as "Month Year"
  const formatLiquidationDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
  }

  // ==========================================
  // USA-SPECIFIC ASSETS
  // ==========================================
  if (isUSA && usaAssets) {
    // HSA - Use for medical expenses before losing US residency
    // IMPORTANT: HSA becomes difficult to use for India medical expenses after leaving US
    if (usaAssets.balanceHSA > 1000) {
    items.push({
      id: 'use-hsa',
      sequence: sequence++,
      asset: 'HSA (Health Savings Account)',
      action: 'Use for qualified medical expenses',
      timing: 'before_move',
      timingDescription: `By ${formatLiquidationDate(sixMonthsBefore)}`,
      reason: 'Cannot easily use HSA for India medical expenses after leaving US',
      estimatedValueUSD: usaAssets.balanceHSA,
      estimatedValueINR: Math.round(usaAssets.balanceHSA * exchangeRate),
      taxImplication: 'Tax-free if used for qualified medical expenses',
      requiresCAReview: false
    })
  }

  // RSUs - Sell before move (includes detailed grants + simple rsuEquityValue)
  const rsuGrantsValue = usaAssets.rsuGrants.reduce((sum: number, g: RSUGrant) => sum + g.vestedShares * g.currentPricePerShare, 0)
  const rsuValue = rsuGrantsValue + (usaAssets.rsuEquityValue || 0)
  if (rsuValue > 10000) {
    items.push({
      id: 'liquidate-rsu',
      sequence: sequence++,
      asset: 'RSUs / Company Stock',
      action: 'Sell 100%',
      timing: 'before_move',
      timingDescription: `${formatLiquidationDate(sixMonthsBefore)} - ${formatLiquidationDate(threeMonthsBefore)}`,
      reason: 'US LTCG (15%) < India STCG (20%+)',
      estimatedValueUSD: rsuValue,
      estimatedValueINR: Math.round(rsuValue * exchangeRate),
      taxImplication: `~15% federal LTCG + state tax`,
      requiresCAReview: rsuValue > 100000
    })
  }

  // ESPP - Sell before move (similar to RSUs)
  if (usaAssets.esppBalance > 5000) {
    items.push({
      id: 'liquidate-espp',
      sequence: sequence++,
      asset: 'ESPP Shares',
      action: 'Sell before move',
      timing: 'before_move',
      timingDescription: `${formatLiquidationDate(sixMonthsBefore)} - ${formatLiquidationDate(threeMonthsBefore)}`,
      reason: 'Lock in US tax treatment on discount portion',
      estimatedValueUSD: usaAssets.esppBalance,
      estimatedValueINR: Math.round(usaAssets.esppBalance * exchangeRate),
      taxImplication: 'Discount taxed as income, gains as capital gains',
      requiresCAReview: usaAssets.esppBalance > 50000
    })
  }

  // Taxable brokerage - Harvest gains before move (lowered threshold from $50K)
  if (usaAssets.taxableBrokerage > 10000) {
    items.push({
      id: 'liquidate-brokerage',
      sequence: sequence++,
      asset: 'Taxable Brokerage',
      action: 'Harvest long-term gains',
      timing: 'before_move',
      timingDescription: `${formatLiquidationDate(sixMonthsBefore)} - ${formatLiquidationDate(threeMonthsBefore)}`,
      reason: 'Lock in US LTCG rates before becoming India resident',
      estimatedValueUSD: usaAssets.taxableBrokerage,
      estimatedValueINR: Math.round(usaAssets.taxableBrokerage * exchangeRate),
      taxImplication: '15% LTCG on gains',
      requiresCAReview: true
    })
  }

  // 401k/IRA - Convert during RNOR (lowered threshold from $50K)
  if (rnor?.eligible && (usaAssets.balance401k > 10000 || usaAssets.balanceTraditionalIRA > 10000)) {
    const retirementTotal = usaAssets.balance401k + usaAssets.balanceTraditionalIRA
    const rnorStartDate = new Date(targetYear, 3, 1) // April of return year
    const rnorEndYear = targetYear + rnor.estimatedYears
    items.push({
      id: 'convert-401k',
      sequence: sequence++,
      asset: '401(k) / Traditional IRA',
      action: 'Roth conversion (partial)',
      timing: 'during_rnor',
      timingDescription: `${formatLiquidationDate(rnorStartDate)} - March ${rnorEndYear}`,
      reason: 'RNOR shields conversion from India tax',
      estimatedValueUSD: retirementTotal,
      estimatedValueINR: Math.round(retirementTotal * exchangeRate),
      taxImplication: 'US tax only (~22%), no India tax during RNOR',
      requiresCAReview: true
    })
  }

  // Roth IRA - Keep for last
  if (usaAssets.balanceRothIRA > 10000) {
    items.push({
      id: 'hold-roth',
      sequence: sequence++,
      asset: 'Roth IRA',
      action: 'Hold (withdraw last)',
      timing: 'post_rnor',
      timingDescription: 'Age 59.5+ for tax-free withdrawal',
      reason: 'Tax-free growth, withdraw after 59.5',
      estimatedValueUSD: usaAssets.balanceRothIRA,
      estimatedValueINR: Math.round(usaAssets.balanceRothIRA * exchangeRate),
      taxImplication: 'Tax-free qualified distributions',
      requiresCAReview: false
    })
  }

  // Crypto - Sell before move (US has clearer tax treatment)
  if (usaAssets.crypto > 5000) {
    items.push({
      id: 'liquidate-crypto',
      sequence: sequence++,
      asset: 'Cryptocurrency',
      action: 'Sell before move',
      timing: 'before_move',
      timingDescription: `${formatLiquidationDate(sixMonthsBefore)} - ${formatLiquidationDate(threeMonthsBefore)}`,
      reason: 'US crypto tax rules are clearer; India rules are evolving (30% flat tax)',
      estimatedValueUSD: usaAssets.crypto,
      estimatedValueINR: Math.round(usaAssets.crypto * exchangeRate),
      taxImplication: 'US: 15% LTCG if held >1yr; India: 30% flat rate',
      requiresCAReview: true
    })
  }

  // Stock Options - Exercise before move
  const optionsValue = usaAssets.stockOptions?.reduce((sum, opt) => {
    const inTheMoney = Math.max(0, (opt.currentPrice || 0) - opt.strikePrice) * opt.vestedShares
    return sum + inTheMoney
  }, 0) || 0
  if (optionsValue > 10000) {
    items.push({
      id: 'exercise-options',
      sequence: sequence++,
      asset: 'Stock Options',
      action: 'Exercise and sell (if in-the-money)',
      timing: 'before_move',
      timingDescription: `${formatLiquidationDate(sixMonthsBefore)} - ${formatLiquidationDate(threeMonthsBefore)}`,
      reason: 'Avoid cross-border exercise complexities; lock in US tax treatment',
      estimatedValueUSD: optionsValue,
      estimatedValueINR: Math.round(optionsValue * exchangeRate),
      taxImplication: 'ISO: AMT may apply; NSO: ordinary income + LTCG',
      requiresCAReview: true
    })
  }

  // 529 Plan - Keep or transfer
  if (usaAssets.balance529Plan > 5000) {
    items.push({
      id: 'hold-529',
      sequence: sequence++,
      asset: '529 Education Plan',
      action: 'Keep for US education or beneficiary change',
      timing: 'post_rnor',
      timingDescription: 'No immediate action required',
      reason: 'Can use for US education expenses; or change beneficiary to family member',
      estimatedValueUSD: usaAssets.balance529Plan,
      estimatedValueINR: Math.round(usaAssets.balance529Plan * exchangeRate),
      taxImplication: 'Tax-free for qualified education; 10% penalty + tax if non-qualified',
      requiresCAReview: false
    })
  }

    // Savings/Checking - Transfer to India
    if (usaAssets.savingsChecking > 10000) {
      items.push({
        id: 'transfer-savings',
        sequence: sequence++,
        asset: 'US Bank Savings/Checking',
        action: 'Transfer to NRE account',
        timing: 'before_move',
        timingDescription: `${formatLiquidationDate(threeMonthsBefore)} - ${formatLiquidationDate(targetDate)}`,
        reason: 'NRE deposits are tax-free in India; easier to manage from India',
        estimatedValueUSD: usaAssets.savingsChecking,
        estimatedValueINR: Math.round(usaAssets.savingsChecking * exchangeRate),
        taxImplication: 'No tax implications; repatriable funds',
        requiresCAReview: false
      })
    }
  } // End USA-specific assets

  // ==========================================
  // UAE-SPECIFIC ASSETS
  // ==========================================
  if (isUAE && residentCountryAssets?.assets) {
    const uaeAssets = residentCountryAssets.assets

    // Savings Account - Transfer to NRE
    const savings = uaeAssets['savings'] || uaeAssets['bankSavings'] || 0
    if (savings > 10000) {
      items.push({
        id: 'transfer-uae-savings',
        sequence: sequence++,
        asset: 'UAE Bank Savings',
        action: 'Transfer to NRE account',
        timing: 'before_move',
        timingDescription: `${formatLiquidationDate(threeMonthsBefore)} - ${formatLiquidationDate(targetDate)}`,
        reason: 'NRE deposits are tax-free in India; consolidate funds before move',
        estimatedValueUSD: savings,
        estimatedValueINR: Math.round(savings * exchangeRate),
        taxImplication: 'No tax in UAE or India (NRE)',
        requiresCAReview: false
      })
    }

    // Investment/Brokerage - Liquidate before move
    const investments = uaeAssets['investments'] || uaeAssets['stocksBrokerage'] || 0
    if (investments > 10000) {
      items.push({
        id: 'liquidate-uae-investments',
        sequence: sequence++,
        asset: 'UAE Investments/Stocks',
        action: 'Liquidate before move',
        timing: 'before_move',
        timingDescription: `${formatLiquidationDate(sixMonthsBefore)} - ${formatLiquidationDate(threeMonthsBefore)}`,
        reason: 'No capital gains tax in UAE; may be taxed in India post-RNOR',
        estimatedValueUSD: investments,
        estimatedValueINR: Math.round(investments * exchangeRate),
        taxImplication: 'Tax-free in UAE; plan timing for RNOR benefit',
        requiresCAReview: true
      })
    }

    // Mutual Funds
    const mutualFunds = uaeAssets['mutualFunds'] || 0
    if (mutualFunds > 10000) {
      items.push({
        id: 'liquidate-uae-mf',
        sequence: sequence++,
        asset: 'UAE Mutual Funds',
        action: 'Redeem during RNOR',
        timing: 'during_rnor',
        timingDescription: `${formatLiquidationDate(targetDate)} - RNOR period`,
        reason: 'RNOR shields foreign mutual fund gains from India tax',
        estimatedValueUSD: mutualFunds,
        estimatedValueINR: Math.round(mutualFunds * exchangeRate),
        taxImplication: 'Tax-free during RNOR window',
        requiresCAReview: true
      })
    }

    // RSUs (many UAE tech workers have RSUs)
    const rsus = uaeAssets['rsus'] || uaeAssets['employerStock'] || 0
    if (rsus > 10000) {
      items.push({
        id: 'liquidate-uae-rsus',
        sequence: sequence++,
        asset: 'RSUs / Employer Stock',
        action: 'Sell before move',
        timing: 'before_move',
        timingDescription: `${formatLiquidationDate(sixMonthsBefore)} - ${formatLiquidationDate(threeMonthsBefore)}`,
        reason: 'No capital gains tax in UAE; avoid India STCG',
        estimatedValueUSD: rsus,
        estimatedValueINR: Math.round(rsus * exchangeRate),
        taxImplication: 'Tax-free in UAE',
        requiresCAReview: false
      })
    }

    // EOSB (End of Service Benefits) - UAE specific
    const eosb = uaeAssets['eosb'] || uaeAssets['endOfServiceBenefit'] || 0
    if (eosb > 5000) {
      items.push({
        id: 'collect-eosb',
        sequence: sequence++,
        asset: 'End of Service Benefits (EOSB)',
        action: 'Collect from employer',
        timing: 'before_move',
        timingDescription: `Upon resignation / ${formatLiquidationDate(targetDate)}`,
        reason: 'Mandatory employer payout; transfer to NRE for tax-free status',
        estimatedValueUSD: eosb,
        estimatedValueINR: Math.round(eosb * exchangeRate),
        taxImplication: 'Tax-free in UAE; tax-free in India if deposited to NRE',
        requiresCAReview: false
      })
    }

    // UAE Property
    const property = uaeAssets['uaePropertyEquity'] || uaeAssets['property'] || 0
    if (property > 50000) {
      items.push({
        id: 'uae-property-decision',
        sequence: sequence++,
        asset: 'UAE Real Estate',
        action: 'Decide: Sell or retain as investment',
        timing: 'before_move',
        timingDescription: `Decision by ${formatLiquidationDate(sixMonthsBefore)}`,
        reason: 'No CGT in UAE if selling; rental income taxable in India post-RNOR',
        estimatedValueUSD: property,
        estimatedValueINR: Math.round(property * exchangeRate),
        taxImplication: 'Selling: Tax-free in UAE. Holding: Rental taxable in India',
        requiresCAReview: true
      })
    }

    // Crypto
    const crypto = uaeAssets['crypto'] || 0
    if (crypto > 5000) {
      items.push({
        id: 'liquidate-uae-crypto',
        sequence: sequence++,
        asset: 'Cryptocurrency',
        action: 'Sell before move',
        timing: 'before_move',
        timingDescription: `${formatLiquidationDate(sixMonthsBefore)} - ${formatLiquidationDate(threeMonthsBefore)}`,
        reason: 'No crypto tax in UAE; India has 30% flat tax on crypto gains',
        estimatedValueUSD: crypto,
        estimatedValueINR: Math.round(crypto * exchangeRate),
        taxImplication: 'Tax-free in UAE vs 30% in India',
        requiresCAReview: true
      })
    }
  } // End UAE-specific assets

  return items
}

function generateMilestones(
  state: CalculatorState,
  dates: ComputedJourneyData['dates'],
  rnor: QuickRNORResult | null
): Milestone[] {
  const { profile } = state
  const milestones: Milestone[] = []
  const currentDate = new Date()
  const targetDate = new Date(profile.expectedReturnYear || currentDate.getFullYear() + 2, 3, 1)

  // Helper to calculate days from now
  const daysFrom = (date: Date) => Math.floor((date.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24))

  // 6 months before move - Open NRE account
  const sixMonthsBefore = new Date(targetDate)
  sixMonthsBefore.setMonth(sixMonthsBefore.getMonth() - 6)
  if (sixMonthsBefore > currentDate) {
    milestones.push({
      id: 'open-nre',
      date: formatDate(sixMonthsBefore),
      title: 'Open NRE/NRO accounts',
      description: 'Set up India bank accounts for fund transfers',
      category: 'preparation',
      isDeadline: false,
      daysFromNow: daysFrom(sixMonthsBefore),
      urgency: daysFrom(sixMonthsBefore) < 30 ? 'high' : 'medium'
    })
  }

  // 3 months before - Start asset liquidation
  const threeMonthsBefore = new Date(targetDate)
  threeMonthsBefore.setMonth(threeMonthsBefore.getMonth() - 3)
  if (threeMonthsBefore > currentDate) {
    milestones.push({
      id: 'start-liquidation',
      date: formatDate(threeMonthsBefore),
      title: 'Begin asset liquidation',
      description: 'Start selling RSUs, harvesting gains',
      category: 'preparation',
      isDeadline: true,
      daysFromNow: daysFrom(threeMonthsBefore),
      urgency: daysFrom(threeMonthsBefore) < 30 ? 'critical' : 'high'
    })
  }

  // Move date
  milestones.push({
    id: 'move-date',
    date: dates.targetMoveDate,
    title: 'Arrive in India',
    description: 'Start of your India chapter',
    category: 'transition',
    isDeadline: true,
    daysFromNow: dates.daysUntilMove,
    urgency: dates.daysUntilMove < 60 ? 'critical' : 'high'
  })

  // RNOR end date
  if (rnor?.eligible && dates.rnorEndDate) {
    milestones.push({
      id: 'rnor-end',
      date: dates.rnorEndDate,
      title: 'RNOR period ends',
      description: 'Foreign income becomes taxable in India',
      category: 'rnor',
      isDeadline: true,
      daysFromNow: daysFrom(new Date(dates.rnorEndDate)),
      urgency: 'medium'
    })
  }

  return milestones.sort((a, b) => a.daysFromNow - b.daysFromNow)
}

function generateRiskFlags(state: CalculatorState, totalForeignAssets: number): RiskFlag[] {
  const { profile } = state
  // Use helper to get effective USA assets
  const usaAssets = getEffectiveUSAAssets(state)
  const flags: RiskFlag[] = []

  // Concentration risk - RSUs (includes detailed grants + simple rsuEquityValue)
  const rsuGrantsValue = usaAssets.rsuGrants.reduce((sum: number, g: RSUGrant) => sum + g.vestedShares * g.currentPricePerShare, 0)
  const rsuValue = rsuGrantsValue + (usaAssets.rsuEquityValue || 0)
  if (rsuValue > 0 && totalForeignAssets > 0) {
    const concentration = rsuValue / totalForeignAssets
    if (concentration > 0.5) {
      flags.push({
        id: 'rsu-concentration',
        type: 'concentration',
        severity: concentration > 0.7 ? 'high' : 'medium',
        title: 'High RSU Concentration',
        description: `RSUs represent ${Math.round(concentration * 100)}% of your net worth`,
        recommendation: 'Consider diversifying before move to reduce single-stock risk',
        relatedAsset: 'RSUs',
        amountAtRisk: rsuValue
      })
    }
  }

  // Currency exposure
  if (totalForeignAssets > 500000 * profile.exchangeRate) {
    flags.push({
      id: 'currency-exposure',
      type: 'currency',
      severity: 'medium',
      title: 'Currency Exposure',
      description: `Significant USD assets moving to INR expenses`,
      recommendation: 'Consider gradual conversion or maintaining USD investments for hedging'
    })
  }

  // Timing window risk
  const targetMonth = 4 // April
  const targetYear = profile.expectedReturnYear || new Date().getFullYear() + 2
  const targetDate = new Date(targetYear, targetMonth - 1, 1)
  const fyEndDate = new Date(targetYear, 2, 31) // March 31
  const daysBuffer = Math.abs((targetDate.getTime() - fyEndDate.getTime()) / (1000 * 60 * 60 * 24))

  if (daysBuffer < 30) {
    flags.push({
      id: 'timing-window',
      type: 'timing',
      severity: 'high',
      title: 'Tight Timing Window',
      description: `Target move date is close to FY end boundary`,
      recommendation: 'Consider moving earlier to ensure you capture the full RNOR benefit for this FY'
    })
  }

  return flags
}

function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}

function getFY(year: number, month: number): string {
  if (month >= 4) {
    return `FY ${year}-${(year + 1).toString().slice(-2)}`
  }
  return `FY ${year - 1}-${year.toString().slice(-2)}`
}

function generateInsightSeeds(
  state: CalculatorState,
  taxSavings: TaxSavingsBreakdown,
  socialSecurity: SSEligibilityResult | null,
  riskFlags: RiskFlag[],
  rnor: QuickRNORResult | null
): InsightSeed[] {
  const { profile } = state
  // Use helper to get effective USA assets
  const usaAssets = getEffectiveUSAAssets(state)
  const seeds: InsightSeed[] = []
  const exchangeRate = profile.exchangeRate || DEFAULT_EXCHANGE_RATE

  // Helper to format INR
  const formatINR = (amount: number): string => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`
    return `₹${amount.toLocaleString('en-IN')}`
  }

  // 1. Biggest tax savings opportunity
  if (taxSavings.items.length > 0) {
    const biggestItem = taxSavings.items.reduce((max, item) =>
      item.amountINR > max.amountINR ? item : max
    )
    seeds.push({
      type: 'opportunity',
      title: `${biggestItem.category} - Biggest Tax Savings`,
      keyData: `${biggestItem.amountFormatted} potential savings during RNOR window`,
      relatedAsset: biggestItem.category.includes('401') ? '401k' :
                    biggestItem.category.includes('RSU') ? 'RSU' :
                    biggestItem.category.includes('Brokerage') ? 'Brokerage' : undefined,
      amountFormatted: biggestItem.amountFormatted,
      urgency: 'during_rnor'
    })
  }

  // 2. Social Security opportunity (if close to eligibility)
  if (socialSecurity && socialSecurity.yearsToQualify <= 3 && socialSecurity.yearsToQualify > 0) {
    const ssLifetimeFormatted = formatINR(socialSecurity.lifetimeValueINR)
    seeds.push({
      type: 'opportunity',
      title: `${socialSecurity.yearsToQualify} Years to SS Eligibility`,
      keyData: `Delay return by ${socialSecurity.yearsToQualify} year(s) to qualify for ${ssLifetimeFormatted} in lifetime benefits`,
      amountFormatted: ssLifetimeFormatted,
      urgency: 'before_return'
    })
  }

  // 3. RSU concentration risk (includes detailed grants + simple rsuEquityValue)
  const rsuGrantsValue = usaAssets.rsuGrants.reduce((sum: number, g: RSUGrant) => sum + g.vestedShares * g.currentPricePerShare, 0)
  const rsuValue = rsuGrantsValue + (usaAssets.rsuEquityValue || 0)
  const totalAssets = calculateTotalAssets(state)
  if (rsuValue > 0 && totalAssets > 0) {
    const concentration = rsuValue / totalAssets
    if (concentration > 0.4) {
      seeds.push({
        type: 'warning',
        title: 'RSU Concentration Risk',
        keyData: `RSUs are ${Math.round(concentration * 100)}% of net worth ($${Math.round(rsuValue / 1000)}K) - consider diversifying before move`,
        relatedAsset: 'RSU',
        amountFormatted: formatINR(rsuValue * exchangeRate),
        urgency: 'before_return'
      })
    }
  }

  // 4. Large 401k/IRA opportunity during RNOR
  const retirementTotal = usaAssets.balance401k + usaAssets.balanceTraditionalIRA
  if (rnor?.eligible && retirementTotal > 100000) {
    const potentialSavings = retirementTotal * 0.30 * rnor.estimatedYears * exchangeRate * 0.5 // Rough estimate
    seeds.push({
      type: 'opportunity',
      title: '401(k)/IRA RNOR Conversion Window',
      keyData: `$${Math.round(retirementTotal / 1000)}K in retirement accounts can be converted during ${rnor.estimatedYears}-year RNOR window`,
      relatedAsset: '401k',
      amountFormatted: formatINR(potentialSavings),
      urgency: 'during_rnor'
    })
  }

  // 5. High severity risk flags
  const highRisks = riskFlags.filter(r => r.severity === 'high')
  const topRisk = highRisks[0]
  if (topRisk) {
    seeds.push({
      type: 'warning',
      title: topRisk.title,
      keyData: topRisk.description,
      relatedAsset: topRisk.relatedAsset,
      amountFormatted: topRisk.amountAtRisk ? formatINR(topRisk.amountAtRisk * exchangeRate) : undefined,
      urgency: 'immediate'
    })
  }

  // 6. HSA tip (if they have one)
  if (usaAssets.balanceHSA > 5000) {
    seeds.push({
      type: 'tip',
      title: 'HSA Usage Before Move',
      keyData: `Use $${Math.round(usaAssets.balanceHSA / 1000)}K HSA for medical expenses before losing US residency - cannot easily use in India`,
      relatedAsset: 'HSA',
      amountFormatted: formatINR(usaAssets.balanceHSA * exchangeRate),
      urgency: 'before_return'
    })
  }

  return seeds.slice(0, 5) // Return top 5 seeds
}

function calculateTotalAssets(state: CalculatorState): number {
  // Use helper to get effective USA assets
  const usaAssets = getEffectiveUSAAssets(state)
  let total = usaAssets.balance401k +
    usaAssets.balanceRothIRA +
    usaAssets.balanceTraditionalIRA +
    usaAssets.balanceHSA +
    usaAssets.taxableBrokerage +
    usaAssets.crypto +
    usaAssets.savingsChecking +
    usaAssets.balance529Plan +
    usaAssets.esppBalance +
    (usaAssets.rsuEquityValue || 0)  // Simple RSU value for users who don't enter detailed grants

  total += usaAssets.rsuGrants.reduce((sum: number, grant: RSUGrant) => {
    return sum + (grant.vestedShares * grant.currentPricePerShare)
  }, 0)

  total += Math.max(0, usaAssets.primaryHomeValue - usaAssets.primaryHomeMortgage)
  total += Math.max(0, usaAssets.rentalPropertyValue - usaAssets.rentalPropertyMortgage)

  return total
}

// ==========================================
// Confidence Score Calculator
// ==========================================

/**
 * Calculate deterministic confidence score based on data quality and assumptions.
 *
 * Components (weights):
 * - RNOR Certainty (30%): Is RNOR eligibility verified or estimated?
 * - Asset Data Completeness (25%): How much financial data is filled in?
 * - Tax Calculation Accuracy (25%): Are we using actual RSU gains or assumptions?
 * - Projection Reliability (20%): Time horizon and market volatility impact
 */
function calculateConfidenceScore(
  state: CalculatorState,
  rnor: QuickRNORResult | null,
  taxSavings: TaxSavingsBreakdown,
  usaAssets: ReturnType<typeof getEffectiveUSAAssets>
): ConfidenceScoreBreakdown {
  const limitations: string[] = []
  const improvementSuggestions: string[] = []

  // 1. RNOR Certainty (weight: 30%)
  // - 95% if RNOR was calculated with actual NRI since year
  // - 85% if eligible but using estimated dates
  // - 70% if not eligible (certainty is still high, just different outcome)
  // - 50% if missing data
  let rnorCertainty = 50
  if (rnor) {
    if (state.profile.nriSinceYear && state.rnorResult) {
      // User provided NRI since year AND we have a computed result
      rnorCertainty = rnor.eligible ? 95 : 90 // High certainty either way
    } else if (rnor.eligible) {
      rnorCertainty = 85
      limitations.push('RNOR eligibility based on estimated NRI since year')
      improvementSuggestions.push('Provide your exact NRI since year for higher accuracy')
    } else {
      rnorCertainty = 80
    }
  } else {
    limitations.push('RNOR eligibility could not be determined')
    improvementSuggestions.push('Provide NRI since year and expected return year')
  }

  // 2. Asset Data Completeness (weight: 25%)
  // Check how many major asset categories are filled in
  const assetFields = [
    usaAssets.balance401k > 0,
    usaAssets.balanceRothIRA > 0 || usaAssets.balanceTraditionalIRA > 0,
    usaAssets.taxableBrokerage > 0,
    usaAssets.savingsChecking > 0,
    usaAssets.rsuGrants.length > 0,
    state.indiaAssets.mutualFunds > 0 || state.indiaAssets.stocks > 0,
    state.indiaAssets.nreAccountBalance > 0 || state.indiaAssets.nroAccountBalance > 0,
  ]
  const filledAssetCount = assetFields.filter(Boolean).length
  const assetDataCompleteness = Math.min(100, Math.round((filledAssetCount / assetFields.length) * 100))

  if (assetDataCompleteness < 70) {
    limitations.push('Some major asset categories not provided')
    improvementSuggestions.push('Add details for 401k, brokerage, RSUs, and India investments')
  }

  // 3. Tax Calculation Accuracy (weight: 25%)
  // - RSU gains are estimated at 50% (medium accuracy)
  // - If user has RSUs, we're using assumptions
  // - If no RSUs, calculation is more straightforward
  let taxCalculationAccuracy = 80
  const hasRSUs = usaAssets.rsuGrants.length > 0 || (usaAssets.rsuEquityValue || 0) > 0
  const rsuGrantsValue = usaAssets.rsuGrants.reduce((sum, g) => sum + g.vestedShares * g.currentPricePerShare, 0)
  const rsuValue = rsuGrantsValue + (usaAssets.rsuEquityValue || 0)

  if (hasRSUs && rsuValue > 50000) {
    taxCalculationAccuracy = 65 // Using 50% gain assumption
    limitations.push('RSU gains assumed at 50% of value (actual varies by grant price)')
    improvementSuggestions.push('Verify your actual RSU cost basis for precise tax savings')
  } else if (hasRSUs) {
    taxCalculationAccuracy = 70
  }

  // Check if there are any tax savings calculated
  if (taxSavings.items.length === 0) {
    taxCalculationAccuracy = Math.max(taxCalculationAccuracy - 20, 40)
    limitations.push('No tax savings opportunities identified - may need more data')
  }

  // 4. Projection Reliability (weight: 20%)
  // Based on time horizon - longer = more uncertainty
  const currentYear = new Date().getFullYear()
  const targetYear = state.profile.expectedReturnYear || currentYear + 2
  const yearsUntilMove = Math.max(0, targetYear - currentYear)

  let projectionReliability: number
  if (yearsUntilMove <= 1) {
    projectionReliability = 85 // Near-term, fairly predictable
  } else if (yearsUntilMove <= 3) {
    projectionReliability = 75 // Medium-term
  } else if (yearsUntilMove <= 5) {
    projectionReliability = 65 // Longer-term, more market uncertainty
    limitations.push(`${yearsUntilMove}-year projection subject to market volatility (±15-20%)`)
  } else {
    projectionReliability = 55 // Long-term, significant uncertainty
    limitations.push(`${yearsUntilMove}-year projection has high uncertainty`)
    improvementSuggestions.push('Consider running scenarios annually as conditions change')
  }

  // Calculate weighted overall score
  const weights = {
    rnorCertainty: 0.30,
    assetDataCompleteness: 0.25,
    taxCalculationAccuracy: 0.25,
    projectionReliability: 0.20
  }

  const overall = Math.round(
    rnorCertainty * weights.rnorCertainty +
    assetDataCompleteness * weights.assetDataCompleteness +
    taxCalculationAccuracy * weights.taxCalculationAccuracy +
    projectionReliability * weights.projectionReliability
  )

  // Determine level
  const level: 'high' | 'medium' | 'low' =
    overall >= 80 ? 'high' :
    overall >= 60 ? 'medium' :
    'low'

  // Generate explanation
  let explanation: string
  if (level === 'high') {
    explanation = 'High confidence based on verified RNOR eligibility and comprehensive asset data.'
  } else if (level === 'medium') {
    explanation = 'Medium confidence. Some assumptions used - see limitations below.'
  } else {
    explanation = 'Limited confidence due to missing data. Results are preliminary estimates.'
  }

  return {
    overall,
    level,
    components: {
      rnorCertainty,
      assetDataCompleteness,
      taxCalculationAccuracy,
      projectionReliability
    },
    explanation,
    limitations,
    improvementSuggestions
  }
}
